import { useCallback, useEffect, useState } from "react";
import { Loader2, Users, Check, X, CalendarClock, Timer, CircleDot, FileDown } from "lucide-react";
import { toast } from "sonner";
import OSLayout from "../../components/os/OSLayout";
import { api, mediaUrl } from "../../lib/api";
import { useAuth } from "../../context/AuthContext";
import { Avatar, AvatarFallback, AvatarImage } from "../../components/ui/avatar";
import { Badge } from "../../components/ui/badge";

const ATT = {
  abierto: { label: "Presente", cls: "text-emerald-600" },
  cerrado: { label: "Jornada cerrada", cls: "text-navy/50" },
  sin_registro: { label: "Sin registro", cls: "text-navy/35" },
};

function Emp({ e, right }) {
  return (
    <div className="flex items-center gap-3">
      <Avatar className="h-9 w-9">
        {e.avatar ? <AvatarImage src={mediaUrl(e.avatar)} alt={e.name} /> : null}
        <AvatarFallback className="text-[10px] bg-violet/10 text-violet">{(e.name || "?").split(" ").map((s) => s[0]).slice(0, 2).join("")}</AvatarFallback>
      </Avatar>
      <div className="min-w-0 flex-1">
        <div className="text-sm font-medium text-navy truncate">{e.name}</div>
        <div className="text-xs text-navy/50 truncate">{[e.puesto, e.area].filter(Boolean).join(" · ") || "—"}</div>
      </div>
      {right}
    </div>
  );
}

export default function LeaderPanel() {
  const { user } = useAuth();
  const [team, setTeam] = useState([]);
  const [pending, setPending] = useState({ timeoff: [], timesheets: [] });
  const [loading, setLoading] = useState(true);

  const load = useCallback(() => {
    setLoading(true);
    Promise.all([
      api.get("/people/team").catch(() => ({ data: { team: [] } })),
      api.get("/people/team/pending").catch(() => ({ data: { timeoff: [], timesheets: [] } })),
    ]).then(([tm, pd]) => { setTeam(tm.data.team || []); setPending(pd.data); }).finally(() => setLoading(false));
  }, []);
  useEffect(() => { load(); }, [load]);

  const decideTimeoff = async (id, status) => {
    try { await api.patch(`/people/timeoff/${id}`, { status }); toast.success(status === "approved" ? "Aprobada" : "Rechazada"); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Error"); }
  };
  const decideTimesheet = async (id, status) => {
    try { await api.patch(`/workforce/timesheets/${id}`, { status }); toast.success(status === "aprobado" ? "Aprobado" : "Rechazado"); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Error"); }
  };

  const pendingCount = (pending.timeoff?.length || 0) + (pending.timesheets?.length || 0);

  const downloadReport = async () => {
    try {
      const res = await api.get("/people/team/report.pdf", { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url; a.download = "reporte_equipo.pdf"; a.click();
      window.URL.revokeObjectURL(url);
      toast.success("Reporte generado");
    } catch { toast.error("No se pudo generar el reporte"); }
  };

  return (
    <OSLayout title="Panel del Líder">
      <div className="max-w-5xl space-y-8" data-testid="leader-panel">
        <div className="flex items-start justify-between gap-4">
          <div>
            <div className="overline text-navy/40 mb-2 flex items-center gap-2"><Users className="w-3.5 h-3.5" /> OMNIA People Suite</div>
            <h2 className="font-editorial text-4xl md:text-5xl tracking-tight">Panel del Líder</h2>
            <p className="mt-2 text-navy/50 max-w-2xl">Tu equipo directo y las solicitudes pendientes de aprobación, en un solo lugar.</p>
          </div>
          <button onClick={downloadReport} data-testid="team-report-btn" className="btn btn-primary shrink-0 gap-2"><FileDown className="w-4 h-4" /> Reporte del mes</button>
        </div>

        {loading ? <div className="py-16 flex justify-center text-navy/40"><Loader2 className="w-6 h-6 animate-spin" /></div> : (
          <>
            {/* Pendientes */}
            <section data-testid="leader-pending">
              <h3 className="text-sm font-medium text-navy/70 mb-3 flex items-center gap-2">
                Pendientes de aprobación {pendingCount > 0 && <Badge className="bg-gold/15 text-gold border-none">{pendingCount}</Badge>}
              </h3>
              {pendingCount === 0 ? (
                <div className="bg-white border border-navy/10 rounded-2xl p-6 text-sm text-navy/40">No tienes solicitudes pendientes. 🎉</div>
              ) : (
                <div className="grid gap-3 lg:grid-cols-2">
                  {pending.timeoff.map((t) => (
                    <div key={t.id} className="bg-white border border-navy/10 rounded-2xl p-4" data-testid={`pending-timeoff-${t.id}`}>
                      <div className="flex items-center gap-2 text-xs text-navy/45 mb-2"><CalendarClock className="w-3.5 h-3.5" /> {t.type} · {t.days}d</div>
                      <div className="text-sm font-medium text-navy">{t.user_name}</div>
                      <div className="text-xs text-navy/50">{t.start?.slice(0, 10)} → {t.end?.slice(0, 10)}</div>
                      {t.reason && <div className="text-xs text-navy/40 mt-1">{t.reason}</div>}
                      <div className="flex gap-2 mt-3">
                        <button onClick={() => decideTimeoff(t.id, "approved")} data-testid={`approve-timeoff-${t.id}`} className="btn btn-primary text-sm flex-1"><Check className="w-4 h-4" /> Aprobar</button>
                        <button onClick={() => decideTimeoff(t.id, "rejected")} className="btn btn-ghost text-sm"><X className="w-4 h-4" /> Rechazar</button>
                      </div>
                    </div>
                  ))}
                  {pending.timesheets.map((t) => (
                    <div key={t.id} className="bg-white border border-navy/10 rounded-2xl p-4" data-testid={`pending-timesheet-${t.id}`}>
                      <div className="flex items-center gap-2 text-xs text-navy/45 mb-2"><Timer className="w-3.5 h-3.5" /> {t.hours}h · {t.date}</div>
                      <div className="text-sm font-medium text-navy">{t.user_name}</div>
                      <div className="text-xs text-navy/50">{t.project_name || t.task || "—"}</div>
                      <div className="flex gap-2 mt-3">
                        <button onClick={() => decideTimesheet(t.id, "aprobado")} data-testid={`approve-timesheet-${t.id}`} className="btn btn-primary text-sm flex-1"><Check className="w-4 h-4" /> Aprobar</button>
                        <button onClick={() => decideTimesheet(t.id, "rechazado")} className="btn btn-ghost text-sm"><X className="w-4 h-4" /> Rechazar</button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </section>

            {/* Mi equipo */}
            <section data-testid="leader-team">
              <h3 className="text-sm font-medium text-navy/70 mb-3">Mi equipo ({team.length})</h3>
              {team.length === 0 ? (
                <div className="bg-white border border-navy/10 rounded-2xl p-6 text-sm text-navy/40">Aún no tienes colaboradores a cargo. Asigna "Reporta a" en el Directorio.</div>
              ) : (
                <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
                  {team.map((e) => {
                    const a = ATT[e.attendance_today] || ATT.sin_registro;
                    return (
                      <div key={e.id} className="bg-white border border-navy/10 rounded-2xl p-4" data-testid={`team-member-${e.id}`}>
                        <Emp e={e} right={<CircleDot className={`w-4 h-4 ${a.cls}`} />} />
                        <div className={`mt-2 text-xs ${a.cls}`}>{a.label}</div>
                      </div>
                    );
                  })}
                </div>
              )}
            </section>
          </>
        )}
      </div>
    </OSLayout>
  );
}
